import React, { useCallback } from 'react';
import styled from 'styled-components';
import placeholderImage from 'images/logo-placeholder.svg';
import adminImage from 'images/vendorSelector/admin.png';

const LogoContainer = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
`;

const BrandLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

interface BrandLogoDisplayProps {
  logoUrl: string | null;
  brandName?: string;
  isAdminView?: boolean;
}

/**
 * Brand logo display component with error handling
 * 
 * Features:
 * - Fallback to placeholder image on error
 * - Proper image sizing and positioning
 * - Accessibility with alt text
 */
const BrandLogoDisplay: React.FC<BrandLogoDisplayProps> = React.memo(({
  logoUrl,
  brandName = 'Brand',
  isAdminView = false
}) => {
  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.currentTarget;
    if (target && target.src !== placeholderImage) {
      target.src = placeholderImage;
    }
  }, []);

  if (isAdminView) {
    return (
      <LogoContainer>
        <BrandLogo
          src={adminImage}
          alt="Admin"
        />
      </LogoContainer>
    );
  }

  if (!logoUrl) {
    return (
      <LogoContainer>
        <BrandLogo
          src={placeholderImage}
          alt={brandName}
        />
      </LogoContainer>
    );
  }

  return (
    <LogoContainer>
      <BrandLogo
        src={logoUrl}
        alt={brandName}
        onError={handleImageError}
      />
    </LogoContainer>
  );
});

BrandLogoDisplay.displayName = 'BrandLogoDisplay';

export default BrandLogoDisplay;